import {
    Button,
    Dialog,
    DialogBody,
    Divider,
    SegmentedControl,
    Switch
} from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { DownloadMenu } from "components/common/DownloadMenu";
import { ZoomControls } from "components/common/ZoomControls";
import { useCallback, useRef, useState } from "react";

type CostGraphControlsProps = {
    readonly showPerMW: boolean;
    readonly onShowPerMWChange: (value: boolean) => void;
    readonly showCapacityToggle?: boolean;
    readonly includeCapacity?: boolean;
    readonly onIncludeCapacityChange?: () => void;
    readonly xDomain: [number, number] | null;
    readonly onXDomainChange: (domain: [number, number] | null) => void;
    readonly dataBounds: { min: number; max: number };
    readonly onDownloadCSV: () => void;
    readonly onDownloadImage: (
        chartRef: React.RefObject<HTMLDivElement>
    ) => void;
    readonly sidebarChartRef: React.RefObject<HTMLDivElement>;
    readonly title?: string;
    readonly renderExpandedGraph?: (
        chartRef: React.RefObject<HTMLDivElement>
    ) => React.ReactNode;
};

type ControlsBarProps = {
    readonly showPerMW: boolean;
    readonly onShowPerMWChange: (value: boolean) => void;
    readonly showCapacityToggle: boolean;
    readonly includeCapacity?: boolean;
    readonly onIncludeCapacityChange?: () => void;
    readonly xDomain: [number, number] | null;
    readonly onXDomainChange: (domain: [number, number] | null) => void;
    readonly dataBounds: { min: number; max: number };
    readonly onDownloadCSV: () => void;
    readonly onDownloadImage: () => void;
    readonly showExpandButton?: boolean;
    readonly onExpandClick?: () => void;
};

const ControlsBar: React.FC<ControlsBarProps> = ({
    showPerMW,
    onShowPerMWChange,
    showCapacityToggle,
    includeCapacity,
    onIncludeCapacityChange,
    xDomain,
    onXDomainChange,
    dataBounds,
    onDownloadCSV,
    onDownloadImage,
    showExpandButton = false,
    onExpandClick
}) => (
    <div className="CostGraphsSection-controls">
        <div className="CostGraphsSection-controls-switches">
            <SegmentedControl
                size="small"
                value={showPerMW ? "costPerMW" : "cost"}
                onValueChange={(value) =>
                    onShowPerMWChange(value === "costPerMW")
                }
                options={[
                    { label: "Cost Per MW", value: "costPerMW" },
                    { label: "Cost", value: "cost" }
                ]}
            />
            {showCapacityToggle && onIncludeCapacityChange && (
                <Switch
                    checked={includeCapacity}
                    onChange={onIncludeCapacityChange}
                    label="Include Capacity"
                />
            )}
        </div>
        <div className="CostGraphsSection-controls-buttons">
            <ZoomControls
                xDomain={xDomain}
                onXDomainChange={onXDomainChange}
                dataBounds={dataBounds}
            />
            <Divider />
            <DownloadMenu
                onDownloadCSV={onDownloadCSV}
                onDownloadImage={onDownloadImage}
            />
            {showExpandButton && (
                <Button
                    icon={IconNames.FULLSCREEN}
                    variant="minimal"
                    size="small"
                    onClick={onExpandClick}
                    title="Expand graph"
                />
            )}
        </div>
    </div>
);

export const CostGraphControls: React.FC<CostGraphControlsProps> = ({
    showPerMW,
    onShowPerMWChange,
    showCapacityToggle = false,
    includeCapacity,
    onIncludeCapacityChange,
    xDomain,
    onXDomainChange,
    dataBounds,
    onDownloadCSV,
    onDownloadImage,
    sidebarChartRef,
    title = "Cost Graph",
    renderExpandedGraph
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const modalChartRef = useRef<HTMLDivElement>(null);

    const handleExpandClick = useCallback(() => setIsModalOpen(true), []);
    const handleCloseModal = useCallback(() => setIsModalOpen(false), []);

    const handleSidebarDownloadImage = useCallback(() => {
        onDownloadImage(sidebarChartRef);
    }, [onDownloadImage, sidebarChartRef]);

    const handleModalDownloadImage = useCallback(() => {
        onDownloadImage(modalChartRef);
    }, [onDownloadImage]);

    return (
        <>
            <ControlsBar
                showPerMW={showPerMW}
                onShowPerMWChange={onShowPerMWChange}
                showCapacityToggle={showCapacityToggle}
                includeCapacity={includeCapacity}
                onIncludeCapacityChange={onIncludeCapacityChange}
                xDomain={xDomain}
                onXDomainChange={onXDomainChange}
                dataBounds={dataBounds}
                onDownloadCSV={onDownloadCSV}
                onDownloadImage={handleSidebarDownloadImage}
                showExpandButton
                onExpandClick={handleExpandClick}
            />
            <Dialog
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                title={title}
                className="CostGraphsSection-expanded-dialog"
            >
                <DialogBody className="CostGraphsSection-expanded-dialog-body">
                    <ControlsBar
                        showPerMW={showPerMW}
                        onShowPerMWChange={onShowPerMWChange}
                        showCapacityToggle={showCapacityToggle}
                        includeCapacity={includeCapacity}
                        onIncludeCapacityChange={onIncludeCapacityChange}
                        xDomain={xDomain}
                        onXDomainChange={onXDomainChange}
                        dataBounds={dataBounds}
                        onDownloadCSV={onDownloadCSV}
                        onDownloadImage={handleModalDownloadImage}
                    />
                    {renderExpandedGraph ? (
                        renderExpandedGraph(modalChartRef)
                    ) : (
                        <div>No graph content available</div>
                    )}
                </DialogBody>
            </Dialog>
        </>
    );
};
